import * as React from 'react'

import { Box, Icon, Tag, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import isNil from 'lodash/isNil'

import { observer } from 'mobx-react'

import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getActionName } from 'modules/automations/components/Show/actionUtils'
import { ExecutionHistoryAggregateActionState } from 'modules/automations/lib/enums'
import { Action } from 'modules/automations/store/models/Action'
import { ExecutionHistoryAggregateAction } from 'modules/automations/store/models/ExecutionHistoryEntry'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import DescriptionItem from './description_item'
import ExpandableBlock from './expandable_block'
import HistoryTask from './task'

interface StateBadgeProps {
  state: ExecutionHistoryAggregateActionState
}

function StateBadge({ state }: StateBadgeProps) {
  switch (state) {
    case ExecutionHistoryAggregateActionState.NotStarted:
      return <Tag type="malibu">{i18n.t('automations.history.action_state.not-started')}</Tag>
    case ExecutionHistoryAggregateActionState.Running:
      return <Tag type="champagne">{i18n.t('automations.history.action_state.running')}</Tag>
    case ExecutionHistoryAggregateActionState.Stopped:
      return <Tag type="radical">{i18n.t('automations.history.action_state.stopped')}</Tag>
    case ExecutionHistoryAggregateActionState.Passed:
      return <Tag type="lime">{i18n.t('automations.history.action_state.passed')}</Tag>
    case ExecutionHistoryAggregateActionState.Ignored:
      return <Tag type="purple">{i18n.t('automations.history.action_state.ignored')}</Tag>
    case ExecutionHistoryAggregateActionState.Error:
      return (
        <Tag type="radical" solid>
          {i18n.t('automations.history.action_state.error')}
        </Tag>
      )
    default:
      return <Tag type="neutral">{state}</Tag>
  }
}

function formatDate(date: string | null | undefined) {
  if (isNil(date)) {
    return undefined
  }

  return convertDate(date, 'backend_datetime', 'ui_short_with_time')
}

interface ReviewNotImplementedInformationProps {
  action: Action | undefined
}

const ReviewNotImplementedInformation = ({ action }: ReviewNotImplementedInformationProps) => {
  const [open, toggle] = useToggle()

  if (action?.attributes.actionable_type !== 'Automations::Actions::RequestReview') {
    return null
  }

  return (
    <Tooltip.Dialog
      open={open}
      toggle={toggle}
      attachment="top center"
      label={i18n.t('automations.history.action.warning_approval')}
      textAlign="center"
    >
      <Box paddingRight="s8">
        <Icon.Medium icon="alert-circle" color="champagne1000" />
      </Box>
    </Tooltip.Dialog>
  )
}

interface HistoryActionProps {
  aggregateAction: ExecutionHistoryAggregateAction
  customFieldsCollection: CustomFieldsCollection
}

const HistoryAction = ({ aggregateAction, customFieldsCollection }: HistoryActionProps) => {
  const action = aggregateAction.data ? new Action(aggregateAction.data) : undefined

  const title = (
    <Box flexDirection="row" gap="s4">
      <Box flexNoShrink>
        <StateBadge state={aggregateAction.state} />
      </Box>
      <ReviewNotImplementedInformation action={action} />
      <Box alignItems="center" flexDirection="row">
        <Text.Normal weight="bold" size="200">
          {getActionName(action, customFieldsCollection, true)}
        </Text.Normal>
      </Box>
    </Box>
  )

  const taskComponents = Object.values(aggregateAction.tasks).map((task) => (
    <HistoryTask key={task.id} aggregateActionTask={task} />
  ))

  return (
    <ExpandableBlock title={title} defaultOpen>
      {aggregateAction.error && (
        <DescriptionItem
          title={i18n.t('automations.history.action.reason')}
          value={aggregateAction.error}
        />
      )}
      <DescriptionItem
        title={i18n.t('automations.history.action.started_at')}
        value={formatDate(aggregateAction.started_at)}
      />
      <DescriptionItem
        title={i18n.t('automations.history.action.finished_at')}
        value={formatDate(aggregateAction.finished_at)}
      />
      {taskComponents.length > 0 && (
        <Box flexDirection="column" gap="s4">
          <Text.Normal weight="semibold">{i18n.t('automations.history.action.tasks')}</Text.Normal>
          {taskComponents}
        </Box>
      )}
    </ExpandableBlock>
  )
}

export default observer(HistoryAction)
